import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip,
  CircularProgress,
  TextField,
  MenuItem,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { config } from "_helpers";

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString("it-IT") : "-";

// Helper per formattare la data del turno selezionato dalla riga
const getShiftDate = (row) => {
  if (row.daySel && row.monthSel && row.yearSel) {
    return `${String(row.daySel).padStart(2, "0")}/${String(row.monthSel).padStart(2, "0")}/${row.yearSel}`;
  }
  return "-";
};

const StatusChip = ({ confirmedAt, refusedAt }) => {
  if (confirmedAt)
    return <Chip label="Confermato" color="success" size="small" variant="filled" />;
  if (refusedAt)
    return <Chip label="Rifiutato" color="error" size="small" variant="filled" />;
  return <Chip label="In attesa" color="default" size="small" variant="outlined" />;
};

const VacantShiftsAdminDialog = ({
  dataSource = [],
  users = [],
  onCommandClick,
  handleClose,
  isOpen,
  translate = (key) => key,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < config.mobileSizeCheck);
  const [processingId, setProcessingId] = useState(null);

  // Stati per filtraggio e paginazione
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [previousStatusFilter, setPreviousStatusFilter] = useState("pending");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < config.mobileSizeCheck);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset della paginazione al cambio dei filtri o dei dati
  useEffect(() => {
    setPage(0);
  }, [searchTerm, dataSource]);

  const isRowDisabled = (row) => row.confirmedAt !== null || row.refusedAt !== null;

// Filtraggio dinamico dei dati (Volontario, Data e Turno)
  const filteredData = useMemo(() => {
    const query = searchTerm.toLowerCase().trim();

    return dataSource.filter((row) => {
      const loggedUserInfo = users.find((i) => i.anaUserId === row.anaUserId);
      const loggedUserName = loggedUserInfo ? loggedUserInfo.nameSurname.toLowerCase() : "";
      const shiftDate = getShiftDate(row).toLowerCase();
      const shiftDesc = (row.shiftDescription || "").toLowerCase();

      // Ricerca globale stile LIKE su tutti e 3 i campi
      const matchesSearch =
        !query ||
        loggedUserName.includes(query) ||
        shiftDate.includes(query) ||
        shiftDesc.includes(query);

      let matchesStatus = true;
      if (statusFilter === "pending") {
        matchesStatus = !row.confirmedAt && !row.refusedAt;
      } else if (statusFilter === "confirmed") {
        matchesStatus = !!row.confirmedAt;
      } else if (statusFilter === "refused") {
        matchesStatus = !!row.refusedAt;
      }

      return matchesSearch && matchesStatus;
    });
  }, [dataSource, users, searchTerm, statusFilter]);

  // Dati paginati
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  useEffect(() => {
    setPreviousStatusFilter(statusFilter);
  }, [statusFilter]);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAction = async (commandType, rowData) => {
    const rowId = rowData.id || rowData.vacantShiftId || `${rowData.anaUserId}-${rowData.shiftDescription}`;
    try {
      setProcessingId(rowId);
      if (onCommandClick) {
        await onCommandClick({ commandColumn: { type: commandType }, rowData });
      }
    } finally {
      setProcessingId(null);
    }
  };

  const renderActionContent = (row) => {
    const disabled = isRowDisabled(row);
    const rowId = row.id || row.vacantShiftId || `${row.anaUserId}-${row.shiftDescription}`;
    const isLoading = processingId === rowId;

    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 0.5 }}>
          <CircularProgress size={20} color="primary" />
        </Box>
      );
    }

    if (disabled) {
      return (
        <Typography variant="caption" color="text.secondary">
          {row.confirmedAt && `Approvato il ${formatDate(row.confirmedAt)}`}
          {row.refusedAt && `Rifiutato il ${formatDate(row.refusedAt)}`}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
        <Tooltip title={translate("Approva")}>
          <span>
            <IconButton
              size="small"
              color="success"
              disabled={processingId !== null}
              onClick={() => handleAction("confirm", row)}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={translate("Rifiuta")}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={processingId !== null}
              onClick={() => handleAction("refuse", row)}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // BARRA FILTRI
  const renderFilterBar = () => (
    <Box sx={{ display: "flex", gap: 2, mb: 2, flexDirection: isMobile ? "column" : "row" }}>
      <TextField
        size="small"
        placeholder={translate("Cerca per volontario, data o turno...")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth={isMobile}
        sx={{ flex: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        select
        size="small"
        label={translate("Stato")}
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        sx={{ minWidth: isMobile ? "100%" : 160 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FilterListIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      >
        <MenuItem value="all">{translate("Tutti")}</MenuItem>
        <MenuItem value="pending">{translate("In attesa")}</MenuItem>
        <MenuItem value="confirmed">{translate("Confermati")}</MenuItem>
        <MenuItem value="refused">{translate("Rifiutati")}</MenuItem>
      </TextField>
    </Box>
  );

  // TABELLA DESKTOP
  const renderDesktopTable = (data) => (
    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold", color: "#2c5282" }}>Volontario</TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold", color: "#2c5282" }}>Data Turno</TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "#2c5282" }}>Turno Richiesto</TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold", color: "#2c5282" }}>Stato</TableCell>
            <TableCell align="center" sx={{ fontWeight: "bold", color: "#2c5282" }}>Azioni / Esito</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => {
            const disabled = isRowDisabled(row);
            const loggedUserInfo = users.find((i) => i.anaUserId === row.anaUserId);
            const loggedUserName = loggedUserInfo ? loggedUserInfo.nameSurname : "";
            const shiftDate = getShiftDate(row);

            return (
              <TableRow
                key={idx}
                sx={{
                  opacity: disabled ? 0.75 : 1,
                  "&:hover": { bgcolor: "#f0f7ff" },
                  "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {loggedUserName}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {shiftDate}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.shiftDescription || "-"}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={
                      row.confirmedAt
                        ? `Confermato il: ${formatDate(row.confirmedAt)}`
                        : row.refusedAt
                        ? `Rifiutato il: ${formatDate(row.refusedAt)}`
                        : "In attesa di valutazione"
                    }
                  >
                    <Box component="span">
                      <StatusChip confirmedAt={row.confirmedAt} refusedAt={row.refusedAt} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">{renderActionContent(row)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // VISTA MOBILE
  const renderMobileCards = (data) => (
    <Stack spacing={1.5}>
      {data.map((row, idx) => {
        const disabled = isRowDisabled(row);
        const loggedUserInfo = users.find((i) => i.anaUserId === row.anaUserId);
        const loggedUserName = loggedUserInfo ? loggedUserInfo.nameSurname : "";
        const shiftDate = getShiftDate(row);

        return (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              opacity: disabled ? 0.75 : 1,
              bgcolor: disabled ? "#fafafa" : "#fff",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <StatusChip confirmedAt={row.confirmedAt} refusedAt={row.refusedAt} />
              <Box>{renderActionContent(row)}</Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong style={{ color: "#2c5282" }}>Volontario:</strong> {loggedUserName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong style={{ color: "#2c5282" }}>Data Turno:</strong> {shiftDate}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong style={{ color: "#2c5282" }}>Turno:</strong> {row.shiftDescription || "-"}
            </Typography>
          </Paper>
        );
      })}
    </Stack>
  );

  return (
    <Dialog open={isOpen} onClose={processingId ? undefined : handleClose} fullWidth maxWidth="md">
      {/* HEADER */}
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
        <SupervisorAccountIcon sx={{ color: "white" }} />
        <Typography variant="h6" sx={{ color: "white", fontWeight: "bold", flex: 1 }}>
          {translate("Gestione Turni Vacanti / Approvazioni")}
        </Typography>
        <IconButton onClick={handleClose} disabled={processingId !== null} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENUTO */}
      <DialogContent sx={{ pt: "16px !important" }}>
        {dataSource.length > 0 && renderFilterBar()}

        {filteredData.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {dataSource.length === 0 ? "Nessuna richiesta da gestire" : "Nessun risultato corrisponde ai filtri impostati"}
            </Typography>
          </Box>
        ) : (
          <>
            {isMobile ? renderMobileCards(paginatedData) : renderDesktopTable(paginatedData)}
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage={translate("Righe per pagina:")}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
              sx={{ borderTop: "1px solid", borderColor: "divider", mt: 1 }}
            />
          </>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Button onClick={handleClose} disabled={processingId !== null} variant="contained" color="primary">
          {translate("okBtn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VacantShiftsAdminDialog;